/**
 * Filter-rule constants for the admin requirement editor.
 *
 * FILTER_FIELDS is the authoritative list of FarmProfile fields that can
 * appear in a FilterCondition. Each entry carries the label, domain type,
 * and (for multi/single domains) the valid option values sourced from the
 * same options.ts that drives the onboarding UI — single source of truth.
 *
 * Subset of the fields the backend accepts (shared/farm-filter/fields.ts):
 * the backend keeps evaluating stored conditions on `combinatorBusiness`,
 * which is no longer offered here. See the FILTER_FIELDS note.
 */

import type { FilterCondition, FilterRuleField } from '@/types/farm-profile';
import {
  PROD_OPTIONS,
  GEO_FILTER_OPTIONS,
  CERT_FILTER_OPTIONS,
  CONTROL_PROGRAM_OPTIONS,
  NOTIFIABLE_OPTIONS,
  ECO_STATUS_OPTIONS,
} from './options';

/** Domain of a filter field: how its value is compared and what UI is shown. */
export type FilterFieldDomain = 'multi' | 'number' | 'single';

export interface FilterFieldDef {
  key: FilterRuleField;
  label: string;
  domain: FilterFieldDomain;
  /** Valid option strings for 'multi' and 'single' domains. Absent for 'number'. */
  options?: string[];
}

/**
 * Ordered list of farm-profile fields an admin can author a FilterCondition on.
 *
 * Production types are flattened from PROD_OPTIONS groups so that every
 * individual item (e.g. "Spannmålsodling") is an addressable option value
 * without needing the admin to know the group structure.
 *
 * `combinatorBusiness` is deliberately absent: the wizard never asked it and the
 * activities live under "Övrig verksamhet" in productionTypes (AG-274). The
 * engine still evaluates stored conditions on it until the column is dropped.
 *
 * Geography and certifications use the *_FILTER_OPTIONS lists: they include
 * values the onboarding UI asks for elsewhere (Naturbetesmark on the mark
 * page, IP levels/inriktningar in the IP panel) but stores in these fields.
 */
export const FILTER_FIELDS: FilterFieldDef[] = [
  {
    key: 'productionTypes',
    label: 'Produktionsgrenar',
    domain: 'multi',
    options: PROD_OPTIONS.flatMap((g) => g.items),
  },
  {
    key: 'geographicFactors',
    label: 'Geografi',
    domain: 'multi',
    options: GEO_FILTER_OPTIONS,
  },
  {
    key: 'certifications',
    label: 'Certifieringar',
    domain: 'multi',
    options: CERT_FILTER_OPTIONS,
  },
  {
    key: 'controlPrograms',
    label: 'Kontrollprogram',
    domain: 'multi',
    options: CONTROL_PROGRAM_OPTIONS,
  },
  {
    key: 'otherNotifiable',
    label: 'Anmälningspliktigt',
    domain: 'multi',
    options: NOTIFIABLE_OPTIONS,
  },
  {
    key: 'animalUnits',
    label: 'Djurenheter',
    domain: 'number',
  },
  {
    key: 'ecoStatus',
    label: 'Ekostatus',
    domain: 'single',
    options: ECO_STATUS_OPTIONS,
  },
];

/**
 * Valid operators for each domain type.
 *
 * - multi:  array fields — any-of, all-of, none-of, or emptiness check.
 * - number: numeric fields — threshold comparisons or exact match.
 * - single: scalar string fields — exact match, one-of / none-of a list
 *           (the engine reads the scalar as a one-element set, AG-275), or
 *           emptiness check. `includesAll` is degenerate on a scalar and is
 *           rejected by the backend, so it is not offered.
 */
export const FIELD_OPERATORS: Record<
  FilterFieldDomain,
  FilterCondition['operator'][]
> = {
  multi: ['includesAny', 'includesAll', 'excludesAny', 'isEmpty'],
  number: ['gte', 'lte', 'eq'],
  single: ['eq', 'includesAny', 'excludesAny', 'isEmpty'],
};

/**
 * Swedish display labels for each operator, framed as outcome language so a
 * condition reads as a sentence after the field name:
 *   "Gårdens [Certifieringar] [har någon av]: KRAV"
 */
export const OPERATOR_LABELS: Record<FilterCondition['operator'], string> = {
  includesAny: 'har någon av',
  includesAll: 'har alla av',
  excludesAny: 'har ingen av',
  isEmpty: 'är inte angivet',
  eq: 'är',
  gte: 'är minst',
  lte: 'är högst',
};

/**
 * Operator label in context of the field's domain. A scalar field "is one of"
 * its values rather than "has any of" them: "Gårdens Ekostatus är någon av".
 */
export function operatorLabel(
  domain: FilterFieldDomain,
  operator: FilterCondition['operator'],
): string {
  if (domain === 'single' && operator === 'includesAny') return 'är någon av';
  if (domain === 'single' && operator === 'excludesAny') return 'är ingen av';
  return OPERATOR_LABELS[operator];
}

/** Which value input a condition needs. Decided by operator first, then domain. */
export type ValueInputKind = 'chips' | 'number' | 'select' | 'none';

/**
 * Pick the value input for a field/operator pair.
 *
 * - `isEmpty` takes no value.
 * - The set operators always take a list, so chips — also on the scalar
 *   domain, where the list means "one of these".
 * - Otherwise the domain decides: number field → number, scalar → select.
 */
export function valueInputKindFor(
  domain: FilterFieldDomain,
  operator: FilterCondition['operator'],
): ValueInputKind {
  if (operator === 'isEmpty') return 'none';
  if (operator === 'includesAny' || operator === 'includesAll' || operator === 'excludesAny') {
    return 'chips';
  }
  switch (domain) {
    case 'number':
      return 'number';
    case 'single':
      return 'select';
    case 'multi':
      return 'chips';
  }
}

/**
 * Returns the default operator for a given field key.
 * Picks the first operator in the domain's operator list.
 *
 * @param fieldKey The FilterRuleField key to look up.
 * @returns The default operator for the field's domain.
 */
export function defaultOperatorFor(
  fieldKey: FilterRuleField,
): FilterCondition['operator'] {
  const def = FILTER_FIELDS.find((f) => f.key === fieldKey);
  if (!def) return 'includesAny';
  // Each FIELD_OPERATORS entry is a non-empty literal array — the cast is safe.
  return FIELD_OPERATORS[def.domain][0] as FilterCondition['operator'];
}

/**
 * Returns a sensible empty default value for a given field key.
 *
 * - multi domain  → empty array (operator will compare against selections)
 * - number domain → 0
 * - single domain → empty string
 *
 * @param fieldKey The FilterRuleField key to look up.
 * @returns The default value appropriate for the field's domain.
 */
export function defaultValueFor(
  fieldKey: FilterRuleField,
): FilterCondition['value'] {
  const def = FILTER_FIELDS.find((f) => f.key === fieldKey);
  if (!def) return [];
  switch (def.domain) {
    case 'multi':
      return [];
    case 'number':
      return 0;
    case 'single':
      return '';
  }
}

/**
 * Empty default value shaped for a specific operator on a field — used when
 * the admin switches operator and the value input changes kind (e.g. Ekostatus
 * `eq` → `includesAny` goes from a select to chips, so `''` becomes `[]`).
 *
 * @param fieldKey The field the condition is on.
 * @param operator The operator being switched to.
 * @returns `[]` for chips, `0` for number, `''` for select; the field default
 *   when the operator takes no value.
 */
export function defaultValueForOperator(
  fieldKey: FilterRuleField,
  operator: FilterCondition['operator'],
): FilterCondition['value'] {
  const def = FILTER_FIELDS.find((f) => f.key === fieldKey);
  if (!def) return [];
  switch (valueInputKindFor(def.domain, operator)) {
    case 'chips':
      return [];
    case 'number':
      return 0;
    case 'select':
      return '';
    case 'none':
      return defaultValueFor(fieldKey);
  }
}
